import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/context/NotificationContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Bell, Check, CheckCheck, Trash2, X,
  MessageCircle, CheckSquare, Share2, Info, AlertCircle, BellRing
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const typeIcons = {
  task: CheckSquare,
  message: MessageCircle,
  transfer: Share2,
  system: AlertCircle,
  info: Info
};

const typeColors = {
  task: 'text-[#FF9500]',
  message: 'text-[#002FA7]',
  transfer: 'text-[#AF52DE]',
  system: 'text-[#FF3B30]',
  info: 'text-[#34C759]'
};

const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    pushPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestPushPermission
  } = useNotifications();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.notification_id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-center">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-[#4B5563]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-[#E5E7EB] z-50 animate-in slide-in-from-top-2 duration-200" data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-[#0A0A0A]">Notifications</h3>
              {unreadCount > 0 && (
                <Badge className="bg-[#FF3B30] text-white text-[10px] px-1.5 py-0">{unreadCount}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-[#002FA7] h-7"
                  onClick={markAllAsRead}
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Notifications list */}
          <ScrollArea className="max-h-96">
            {notifications.length === 0 ? (
              <div className="py-12 text-center text-[#9CA3AF]">
                <Bell className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-[#F3F4F6]">
                {notifications.map((notif) => {
                  const Icon = typeIcons[notif.type] || Info;
                  const color = typeColors[notif.type] || 'text-[#9CA3AF]';
                  return (
                    <div
                      key={notif.notification_id}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-[#F9FAFB] ${
                        !notif.is_read ? 'bg-[#002FA7]/[0.03]' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                      data-testid={`notification-item-${notif.notification_id}`}
                    >
                      <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notif.is_read ? 'font-semibold text-[#0A0A0A]' : 'text-[#4B5563]'}`}>
                            {notif.title}
                          </p>
                          {!notif.is_read && (
                            <div className="w-2 h-2 bg-[#002FA7] rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-[#9CA3AF] mt-0.5 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-[#9CA3AF] mt-1">
                          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      <button
                        className="p-1 rounded hover:bg-[#F3F4F6] text-[#9CA3AF] hover:text-[#FF3B30] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notif.notification_id);
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Push notification enable banner */}
          {'Notification' in window && pushPermission !== 'granted' && pushPermission !== 'denied' && (
            <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#F9FAFB]">
              <button
                onClick={requestPushPermission}
                className="flex items-center gap-2 text-xs text-[#002FA7] hover:text-[#00227A] font-medium w-full"
                data-testid="enable-push-btn"
              >
                <BellRing className="w-3.5 h-3.5" />
                Enable push notifications for background alerts
              </button>
            </div>
          )}
          {pushPermission === 'granted' && (
            <div className="px-4 py-2 border-t border-[#E5E7EB] bg-[#F9FAFB]">
              <p className="text-[10px] text-[#34C759] flex items-center gap-1">
                <BellRing className="w-3 h-3" />
                Push notifications enabled
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
